import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { cn } from '../../lib/utils';
import { FilePreviewInApp } from './panel/useFilePreview';
import { useFileUrl } from './useFileUrl';
import EquipmentTab from './panel/EquipmentTab';
import { MRIMachinePopulated, CompatibilityAnnotations } from './types';
import RoomPanelHeader from './panel/RoomPanelHeader';
import SpecTable from './panel/SpecTable';
import DeleteRoomDialog from './panel/DeleteRoomDialog';

interface MachineDetailViewProps {
    machine: MRIMachinePopulated;
    onEdit?: () => void;
    onClose?: () => void;
    onChange?: () => void;
    readOnly?: boolean;
    compatibility?: CompatibilityAnnotations;
    stickyHeader?: boolean;
}

export default function MachineDetailView({
    machine,
    onEdit,
    onClose,
    onChange,
    readOnly = false,
    compatibility,
    stickyHeader = false,
}: MachineDetailViewProps) {
    const { t } = useTranslation();
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const machineImageUrl = useFileUrl(machine.machineImage?._id);
    const [equipment, setEquipment] = useState(machine.equipment ?? []);

    return (
        <FilePreviewInApp.Provider value={!compatibility}>
            <div className="space-y-6">
                <DeleteRoomDialog
                    open={showDeleteDialog}
                    onOpenChange={setShowDeleteDialog}
                    machineId={machine._id}
                    machineName={machine.Name}
                    equipmentCount={equipment.length}
                    onDeleted={() => {
                        onChange?.();
                        onClose?.();
                    }}
                />

                <div
                    className={cn(
                        'pb-4',
                        stickyHeader &&
                            'sticky -top-[50px] z-10 -mt-[50px] bg-background pt-[50px] dark:bg-neutral-900'
                    )}
                >
                    <RoomPanelHeader
                        machine={machine}
                        imageUrl={machineImageUrl}
                        readOnly={readOnly}
                        onEdit={onEdit}
                        onDelete={() => setShowDeleteDialog(true)}
                    />
                </div>

                <Tabs defaultValue="overview" className="!mt-0">
                    <TabsList className="mb-6 h-auto w-full justify-start gap-6 rounded-none border-x-0 border-y border-subtle bg-transparent p-0 shadow-none">
                        <TabsTrigger
                            value="overview"
                            className="-mb-px rounded-none border-b-2 border-transparent px-0 py-3 text-sm font-medium text-subtle-foreground hover:bg-transparent hover:text-foreground data-[state=active]:border-foreground data-[state=active]:bg-transparent data-[state=active]:text-foreground"
                        >
                            {t('roomOverview.machineDetail.tabs.overview')}
                        </TabsTrigger>
                        <TabsTrigger
                            value="equipment"
                            className="-mb-px gap-2 rounded-none border-b-2 border-transparent px-0 py-3 text-sm font-medium text-subtle-foreground hover:bg-transparent hover:text-foreground data-[state=active]:border-foreground data-[state=active]:bg-transparent data-[state=active]:text-foreground"
                        >
                            {t('roomOverview.machineDetail.tabs.equipment')}
                            <span className="rounded-full bg-muted px-1.5 text-xs tabular-nums dark:bg-neutral-800">
                                {equipment.length}
                            </span>
                        </TabsTrigger>
                    </TabsList>
                    <TabsContent value="overview" className="mt-0">
                        <SpecTable
                            machine={machine}
                            compatibility={compatibility}
                        />
                    </TabsContent>
                    <TabsContent value="equipment" className="mt-0">
                        <EquipmentTab
                            machineId={machine._id}
                            items={equipment}
                            onItemsChange={setEquipment}
                            readOnly={readOnly}
                            onChange={onChange}
                        />
                    </TabsContent>
                </Tabs>
            </div>
        </FilePreviewInApp.Provider>
    );
}
